import { useMemo } from "react";
import dayjs from "dayjs";
import { useGetData } from "@/hooks/services/use-query-hooks";
import { PublicHoliday } from "@/hooks/services/workchestra/handler/public-holiday-api";

/**
 * Fetches active public holidays (all years) once and returns a Map of
 * "YYYY-MM-DD" -> holiday name for O(1) per-day lookup. Cached by react-query,
 * so multiple callers (every calendar grid) share a single request.
 *
 * Used by the job-scheduler calendars to shade public holidays yellow and to
 * render the holiday name in day headers (WOR-2187).
 */
export const useHolidays = (): Map<string, string> => {
  const { data } = useGetData(
    PublicHoliday({
      currentPage: 1,
      itemsPerPage: 1000,
      searchText: "",
      searchYear: 0 // 0 = all years
    }),
    // Public holidays are static reference data shared by every calendar grid/row. Mark the
    // query non-stale so it never auto-refetches on mount/focus — this avoids re-render
    // amplification across the many per-row useHolidays() observers (WOR-2187). Invalidate the
    // ["PublicHoliday", …] key after holiday admin edits if immediate refresh is needed.
    { staleTime: Infinity, refetchOnWindowFocus: false }
  );

  return useMemo(() => {
    const map = new Map<string, string>();
    for (const holiday of data?.items ?? []) {
      if (holiday?.isActive === false) continue;
      const date = dayjs(holiday.publicHolidayDate);
      if (date.isValid()) {
        map.set(date.format("YYYY-MM-DD"), holiday.publicHolidayName);
      }
    }
    return map;
  }, [data]);
};
